import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR, enUS } from "date-fns/locale";
import { IconButton, TextField } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

export interface KeyDriveDatePickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  ariaLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

const iconColors = {
  default: { border: "#2D2D2D33", icon: "rgba(45,45,45,0.5)", bg: "transparent" },
  hover:   { border: "#005A9E80", icon: "rgba(0,90,158,0.5)", bg: "rgba(0,90,158,0.05)" },
  open:    { border: "#005A9E",   icon: "#005A9E",            bg: "rgba(0,90,158,0.05)" },
};

const KeyDriveDatePicker = ({
  label,
  value,
  onChange,
  ariaLabel,
  minDate,
  maxDate,
  disabled,
}: KeyDriveDatePickerProps) => {
  const { i18n } = useTranslation();
  const [state, setState] = useState<"default" | "hover" | "open">("default");

  const locale = i18n.language === "pt-BR" ? ptBR : enUS;
  const inputFormat = i18n.language === "pt-BR" ? "dd/MM/yyyy" : "MM/dd/yyyy";
  const colors = iconColors[state];

  const handleChange = (newValue: unknown) => {
    onChange(newValue as Date | null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        label={label}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        inputFormat={inputFormat}
        onOpen={() => setState("open")}
        onClose={() => setState("default")}
        PopperProps={{
          sx: {
            marginTop: "10px !important",
            "& .MuiPaper-root": {
              border: "1px solid #005A9E",
              borderRadius: "6px",
            },
          },
          disablePortal: true,
        }}
        components={{
          OpenPickerIcon: () => (
            <IconButton
              size="small"
              sx={{
                width: 30,
                height: 30,
                color: colors.icon,
                backgroundColor: colors.bg,
                borderRadius: "50%",
                transition: "background 0.15s, color 0.15s",
              }}
            >
              <CalendarTodayIcon fontSize="small" />
            </IconButton>
          ),
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
            variant="standard"
            onMouseEnter={() => { if (state !== "open") setState("hover"); }}
            onMouseLeave={() => { if (state !== "open") setState("default"); }}
            fullWidth
            sx={{
              minWidth: 155,
              "& .MuiInput-root": {
                height: 44,
                fontSize: 16,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: colors.border,
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: colors.border,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#005A9E",
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default KeyDriveDatePicker;